import { useState, useEffect, useContext } from "react";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";

const useWhatsApps = () => {
  const { user } = useContext(AuthContext);
  const userId = user?.id;
  const companyId = user?.companyId;
  const scope = userId && companyId ? `${companyId}:${userId}` : null;
  const [snapshot, setSnapshot] = useState({ scope: null, whatsApps: [], loading: true });

  useEffect(() => {
    if (!scope) return undefined;
    let active = true;
    let revision = 0;
    let requestNumber = 0;
    let refreshTimer = null;
    let initialErrorShown = false;
    const belongsToCompany = connection => connection &&
      (connection.companyId == null || String(connection.companyId) === String(companyId));

    setSnapshot({ scope, whatsApps: [], loading: true });
    const scheduleRefresh = () => {
      if (!active || refreshTimer) return;
      refreshTimer = setTimeout(() => { refreshTimer = null; refresh(); }, 150);
    };
    const refresh = async () => {
      const request = ++requestNumber;
      const version = revision;
      try {
        const { data } = await api.get("/whatsapp/");
        if (!active || request !== requestNumber) return;
        // A response started before a socket event must not undo its newer status.
        if (version !== revision) { scheduleRefresh(); return; }
        setSnapshot({ scope, whatsApps: data.filter(belongsToCompany), loading: false });
      } catch (err) {
        if (!active || request !== requestNumber) return;
        setSnapshot(current => current.scope === scope ? { ...current, loading: false } : current);
        if (!initialErrorShown) { toastError(err); initialErrorShown = true; }
      }
    };
    const merge = (connection, allowInsert = false) => {
      if (!belongsToCompany(connection) || connection.id == null) return;
      revision += 1;
      setSnapshot(current => {
        if (current.scope !== scope) return current;
        const known = current.whatsApps.some(item => String(item.id) === String(connection.id));
        const whatsApps = known
          ? current.whatsApps.map(item => String(item.id) === String(connection.id) ? { ...item, ...connection } : item)
          : allowInsert ? [...current.whatsApps, connection] : current.whatsApps;
        return { ...current, whatsApps };
      });
      scheduleRefresh();
    };
    const socket = openSocket();
    socket.on("connect", scheduleRefresh);
    socket.on("whatsappSession", data => { if (data.action === "update") merge(data.session); });
    socket.on("whatsappAlert", data => { if (data.action === "update") merge(data.connection); });
    socket.on("whatsapp", data => {
      if (data.action === "update") merge(data.whatsapp, true);
      if (data.action === "delete") {
        revision += 1;
        setSnapshot(current => current.scope === scope ? {
          ...current,
          whatsApps: current.whatsApps.filter(item => String(item.id) !== String(data.whatsappId)),
        } : current);
        scheduleRefresh();
      }
    });
    const visibleRefresh = () => { if (!document.hidden) scheduleRefresh(); };
    const interval = setInterval(visibleRefresh, 30000);
    window.addEventListener("focus", visibleRefresh);
    document.addEventListener("visibilitychange", visibleRefresh);
    scheduleRefresh();
    return () => {
      active = false;
      socket.disconnect();
      clearTimeout(refreshTimer);
      clearInterval(interval);
      window.removeEventListener("focus", visibleRefresh);
      document.removeEventListener("visibilitychange", visibleRefresh);
    };
  }, [scope, companyId]);

  return {
    whatsApps: snapshot.scope === scope && scope ? snapshot.whatsApps : [],
    loading: !!scope && (snapshot.scope !== scope || snapshot.loading),
  };
};

export default useWhatsApps;
